interface PasswordOptions {
  minLength?: number;
  requireUppercase?: boolean;
  requireLowercase?: boolean;
  requireNumbers?: boolean;
  requireSpecialChars?: boolean;
}

interface ValidationErrors {
  [key: string]: string;
}

interface LoginFormValues {
  email: string;
  password: string;
}

interface RegistrationFormValues {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface ForgotPasswordFormValues {
  email: string;
}

export const validators = {
  required: (value: any, fieldName: string = 'Field'): string | null => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      return `${fieldName} is required`;
    }
    return null;
  },

  email: (email: string): string | null => {
    if (!email) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? null : 'Please enter a valid email address';
  },

  password: (password: string, options: PasswordOptions = {}): string | null => {
    const {
      minLength = 6,
      requireUppercase = false,
      requireLowercase = false,
      requireNumbers = false,
      requireSpecialChars = false
    } = options;

    if (!password) return null;

    if (password.length < minLength) {
      return `Password must be at least ${minLength} characters long`;
    }

    if (requireUppercase && !/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }

    if (requireLowercase && !/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }

    if (requireNumbers && !/\d/.test(password)) {
      return 'Password must contain at least one number';
    }

    if (requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Password must contain at least one special character';
    }

    return null;
  },

  passwordConfirmation: (password: string, confirmPassword: string): string | null => {
    if (!confirmPassword) return null;
    return password === confirmPassword ? null : 'Passwords do not match';
  },

  minLength: (value: string, minLength: number, fieldName: string = 'Field'): string | null => {
    if (!value) return null;
    return value.length >= minLength ? null : `${fieldName} must be at least ${minLength} characters long`;
  },

  maxLength: (value: string, maxLength: number, fieldName: string = 'Field'): string | null => {
    if (!value) return null;
    return value.length <= maxLength ? null : `${fieldName} must not exceed ${maxLength} characters`;
  },

  phone: (phone: string): string | null => {
    if (!phone) return null;
    const phoneRegex = /^\+?1?[-.\s]?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
    return phoneRegex.test(phone) ? null : 'Please enter a valid phone number';
  }
};

export const validateLoginForm = (values: LoginFormValues): ValidationErrors => {
  const errors: ValidationErrors = {};

  const emailError = validators.required(values.email, 'Email') || validators.email(values.email);
  if (emailError) errors.email = emailError;

  const passwordError = validators.required(values.password, 'Password') || validators.password(values.password);
  if (passwordError) errors.password = passwordError;

  return errors;
};

export const validateRegistrationForm = (values: RegistrationFormValues): ValidationErrors => {
  const errors: ValidationErrors = {};

  const nameError = validators.required(values.name, 'Name') || validators.minLength(values.name, 2, 'Name');
  if (nameError) errors.name = nameError;

  const emailError = validators.required(values.email, 'Email') || validators.email(values.email);
  if (emailError) errors.email = emailError;

  const passwordError = validators.required(values.password, 'Password') || 
    validators.password(values.password, { minLength: 8, requireNumbers: true });
  if (passwordError) errors.password = passwordError;

  const confirmPasswordError = validators.required(values.confirmPassword, 'Password confirmation') ||
    validators.passwordConfirmation(values.password, values.confirmPassword);
  if (confirmPasswordError) errors.confirmPassword = confirmPasswordError;

  return errors;
};

export const validateForgotPasswordForm = (values: ForgotPasswordFormValues): ValidationErrors => {
  const errors: ValidationErrors = {};

  const emailError = validators.required(values.email, 'Email') || validators.email(values.email);
  if (emailError) errors.email = emailError;

  return errors;
};
